import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Grid, 
  Typography, 
  Link, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  CircularProgress, 
  Alert,
  Modal,
  TextField,
  InputAdornment,
  IconButton,
  Fade,
  Backdrop,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import imgUrl from "../../assets/images/imgUrl";
import axios from 'axios';
import { apiServices } from '../../services/apiServices';
import Swal from 'sweetalert2';

const Gifts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Login modal states
  const [openLoginOptionsModal, setOpenLoginOptionsModal] = useState(false);
  const [selectedProductUrl, setSelectedProductUrl] = useState(null);
  const [selectedLoginType, setSelectedLoginType] = useState('peacekeeper'); // 'peacekeeper' or 'referral'
  
  // Login form states
  const [isLoading, setIsLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [peacekeeperId, setPeacekeeperId] = useState('');
  const [peacekeeperPassword, setPeacekeeperPassword] = useState('');
  const [showPeacekeeperPassword, setShowPeacekeeperPassword] = useState(false);
  // Login method states
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  
  // OTP states
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Fetch products from Shopify GraphQL API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.post(
          'https://shop.i-am-peacekeeper.com/api/2024-01/graphql.json',
          {
            query: `{
              products(first: 100) {
                edges {
                  node {
                    id
                    title
                    handle
                    description
                    images(first: 10) {
                      edges {
                        node {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
            }`
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': '7c22456c425294cc474272ed0be13239'
            }
          }
        );

        if (response.data && response.data.data && response.data.data.products) {
          const productsList = response.data.data.products.edges.map(edge => edge.node);
          setProducts(productsList);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message || 'Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Resend OTP timer
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((timer) => timer - 1);
      }, 1000);
    } else if (resendTimer === 0 && interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const startResendTimer = () => {
    setResendTimer(60); // 60 seconds
  };

  const handleSendOtp = async () => {
    if (!peacekeeperId.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your email address',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(peacekeeperId.trim())) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid email address',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    try {
      setOtpLoading(true);
      const response = await apiServices.postAPI('send-login-token', { email: peacekeeperId.trim() });

      if (response.data) {
        setOtpSent(true);
        startResendTimer();
        Swal.fire({
          title: 'OTP Sent',
          text: 'OTP has been sent to your email address',
          icon: 'success',
          showConfirmButton: false,
          timer: 2000,
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          zIndex: 1500,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to send OTP. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    await handleSendOtp();
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter the OTP',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    if (otp.length < 4) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid OTP',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    try {
      setOtpLoading(true);
      const response = await apiServices.postAPI('validate-login-token', {
        email: peacekeeperId.trim(),
        otp: otp.trim()
      });

      if (response.data && response.data.success) {
        // After OTP verification, call shopify-login to get redirect URL
        // OTP login validates the user, so we can proceed with Shopify login
        try {
          const shopifyLoginResponse = await apiServices.postAPI('shopify-login', {
            email: peacekeeperId.trim(),
            password: '' // OTP login doesn't require password
          });

          if (shopifyLoginResponse.data && shopifyLoginResponse.data.success) {
            setOpenLoginOptionsModal(false);
            
            Swal.fire({
              title: 'Success',
              text: 'Login successful!',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500,
              customClass: {
                popup: 'minimal-popup',
                confirmButton: 'minimal-button',
              },
              backdrop: false,
              zIndex: 1500,
            });

            // Redirect to product URL with login parameters
            if (selectedProductUrl) {
              localStorage.removeItem('giftProductUrl');
              if (shopifyLoginResponse.data.redirectUrl) {
                const temp = shopifyLoginResponse.data.redirectUrl.split('?')[1];
                window.open(selectedProductUrl + '?' + temp, '_self');
              } else {
                window.open(selectedProductUrl, '_self');
              }
            } else if (shopifyLoginResponse.data.redirectUrl) {
              window.open(shopifyLoginResponse.data.redirectUrl, '_self');
            } else {
              window.open('https://shop.i-am-peacekeeper.com/', '_self');
            }
          } else {
            throw new Error(shopifyLoginResponse.data?.message || 'Shopify login failed');
          }
        } catch (shopifyError) {
          // If shopify-login fails, still redirect to product (OTP was valid)
          setOpenLoginOptionsModal(false);
          
          Swal.fire({
            title: 'Success',
            text: 'OTP verified successfully!',
            icon: 'success',
            showConfirmButton: false,
            timer: 1500,
            customClass: {
              popup: 'minimal-popup',
              confirmButton: 'minimal-button',
            },
            backdrop: false,
            zIndex: 1500,
          });

          if (selectedProductUrl) {
            localStorage.removeItem('giftProductUrl');
            window.open(selectedProductUrl, '_self');
          } else {
            window.open('https://shop.i-am-peacekeeper.com/', '_self');
          }
        }
      } else {
        throw new Error(response.data?.message || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Invalid OTP. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleProductClick = (handle) => {
    const productUrl = `https://shop.i-am-peacekeeper.com/products/${handle}`;
    // Store the product URL
    setSelectedProductUrl(productUrl);
    localStorage.setItem('giftProductUrl', productUrl);
    // Open login options modal
    setOpenLoginOptionsModal(true);
  };

  const handleReferralLogin = async (e) => {
    e.preventDefault();
    
    if (!referralCode.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your referral code',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    try {
      setIsLoading(true);
      
      const response = await apiServices.getAPI(`user/check-referral/${referralCode.trim()}`);

      if (response.data && response.data.success) {
        const referralData = response.data.data;
        
        setOpenLoginOptionsModal(false);
        
        Swal.fire({
          title: 'Success',
          text: response.data.message || 'Referral verified successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          zIndex: 1500,
        });

        // Redirect to product URL with referral parameters
        if (selectedProductUrl) {
          localStorage.removeItem('giftProductUrl');
          if (referralData.shopify_Referral_ShoppingURL) {
            const temp = referralData.shopify_Referral_ShoppingURL.split('?')[1];
            window.open(selectedProductUrl + '?' + temp, '_self');
          } else {
            window.open(selectedProductUrl, '_self');
          }
        } else if (referralData.shopify_Referral_ShoppingURL) {
          window.open(referralData.shopify_Referral_ShoppingURL, '_self');
        } else {
          window.open('https://shop.i-am-peacekeeper.com/', '_self');
        }
      } else {
        throw new Error(response.data?.message || 'Invalid referral code');
      }
    } catch (error) {
      console.error('Login with Referral ID error:', error);
      Swal.fire({
        title: 'Login Failed',
        text: error.response?.data?.message || error.message || 'Invalid referral code. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeacekeeperLogin = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!peacekeeperId.trim() || !peacekeeperPassword.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter both email and password',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    try {
      setIsLoading(true);
      
      const userEmail = peacekeeperId.trim();
      
      const shopifyLoginResponse = await apiServices.postAPI('shopify-login', {
        email: userEmail,
        password: peacekeeperPassword
      });

      if (shopifyLoginResponse.data && shopifyLoginResponse.data.success) {
        setOpenLoginOptionsModal(false);
        
        Swal.fire({
          title: 'Success',
          text: 'Login successful!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          zIndex: 1500,
        });

        // Redirect to product URL with login parameters
        if (selectedProductUrl) {
          localStorage.removeItem('giftProductUrl');
          if (shopifyLoginResponse.data.redirectUrl) {
            const temp = shopifyLoginResponse.data.redirectUrl.split('?')[1];
            window.open(selectedProductUrl + '?' + temp, '_self');
          } else {
            window.open(selectedProductUrl, '_self');
          }
        } else if (shopifyLoginResponse.data.redirectUrl) {
          window.open(shopifyLoginResponse.data.redirectUrl, '_self');
        } else {
          navigate('/dashboard');
        }
      } else {
        throw new Error(shopifyLoginResponse.data?.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      Swal.fire({
        title: 'Login Failed',
        text: error.response?.data?.message || error.message || 'Invalid email or password. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExternalLink = () => {
    setOpenLoginOptionsModal(false);
    // Redirect to product URL if available, otherwise redirect to shop homepage
    if (selectedProductUrl) {
      window.open(selectedProductUrl, '_self');
    } else {
      window.open('https://shop.i-am-peacekeeper.com/', '_self');
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '90%', md: '800px' },
    maxWidth: '900px',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    outline: 'none',
    maxHeight: '90vh',
    overflow: 'auto',
    p: 4,
  };
  
  // Add style to ensure SweetAlert appears above Modal
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .swal2-container {
        z-index: 1500 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
    {/* Gift Hero Background Section */}
      <Box
        className="programGiftSectionBg commonBannerBg"
        sx={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "white",
          pl: 2,
          pr: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 80,
            left: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={imgUrl.doveImg}
            alt="Peace Dove Logo"
            sx={{ maxWidth: 200, height: "auto", mr: 1 }}
          />
        </Box>
      </Box>

    {/* Gift Main Section */}
      <Box className="giftProgramSubSection">
        <Grid container direction="column" alignItems="center" spacing={2}>
          <Grid
            
            size={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <img src={imgUrl.peaceIcon} className="w-100 peaceImg"></img>
            <Typography variant="h5" className="founder-title mainHeading green-title">
            <i> I Am Peacekeeper </i>  Gifts
            </Typography>

            <Typography variant="h5" className="comingSoongifts mainHeading green-title">
            Coming Soon
            </Typography>
          </Grid>
        </Grid>
        <Box  display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ px: { xs: 2, sm: 3 }, mt: 3 }}
        >
            <img
              src={imgUrl.peaceKeeperGifts}
              className="w-100 peacekeeperInfographic"
              alt="I Am Peacekeeper Gifts"
            ></img>
        </Box>

        {/* Visual separator between hero infographic and products */}
        <Box sx={{ maxWidth: 900, mx: 'auto', mt: 5, px: { xs: 2, sm: 3 } }}>
          <Divider
            sx={{
              '&::before, &::after': {
                borderColor: '#d7d7d8',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 2, sm: 3 }
              }}
            >
              {/* <Box
                component="img"
                src={imgUrl.comingSoon}
                alt="Coming Soon"
                sx={{
                  position: 'absolute',
                  top: { xs: '-40px', sm: '-50px', md: '9px' },
                  left: '-47px',
                  transform: 'translateX(-50%)',
                  width: { xs: '60px', sm: '80px', md: '100px' },
                  height: 'auto',
                  zIndex: 1,
                  pointerEvents: 'none'
                }}
              /> */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: 'var(--secondary-font)',
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.9rem', sm: '1.1rem' },
                  color: '#214F7D',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                Peacekeeper Gift Collection
              </Typography>
            </Box>
          </Divider>
        </Box>

      </Box>

      {/* Products Section */}
      <Box sx={{ py: 6, pl: 2, pr: 0, width: '100%' }}>
       
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress sx={{ color: '#3A8139' }} />
          </Box>
        )}

        {error && (
          <Box sx={{ maxWidth: '600px', mx: 'auto', mb: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && products.length > 0 && (
          <Grid container spacing={2} sx={{ mx: 'auto', pl: { xs: 2, sm: 2, md: 2 }, pr: 0 }}>
            {products.map((product) => {
              const firstImage = product.images?.edges?.[0]?.node;
              const description = product.description 
                ? (product.description.length > 100 
                    ? product.description.substring(0, 100) + '...' 
                    : product.description)
                : 'No description available';

              return (
                <Grid item xs={12} sm={6} md={6} key={product.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6,
                      },
                      borderRadius: 2,
                      overflow: 'hidden',
                      maxWidth: '420px',
                      mx: 'auto'
                    }}
                  >
                    {firstImage && (
                      <CardMedia
                        component="img"
                        sx={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'contain',
                          backgroundColor: '#f5f5f5',
                          padding: '10px'
                        }}
                        image={firstImage.url}
                        alt={firstImage.altText || product.title}
                      />
                    )}
                    
                    <CardContent sx={{ p: 1.5, pt: 0 }}>
                      <Typography
                        variant="subtitle1"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: '#214F7D',
                          mb: 0.5,
                          fontSize: '0.95rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {product.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.8rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {description}
                      </Typography>
                    </CardContent>

                    <Box sx={{ p: 1.5 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product.handle);
                        }}
                        sx={{
                          background: 'linear-gradient(to right, #214F7D, #3A8139)',
                          color: 'white',
                          borderRadius: '50px',
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 500,
                          mb: 1.5,
                          fontSize: '0.875rem',
                          '&:hover': {
                            background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                          }
                        }}
                      >
                        Shop Now
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {!loading && !error && products.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No products available at the moment.
            </Typography>
          </Box>
        )}
      </Box>

             {/* contact section */}
      <Box className="contactSchoolSection">
        <Typography
          variant="h5"
          align="center"
          fontWeight="bold"
          gutterBottom
          className="subHeading"
          mt={5}
          style={{marginBottom:"2px"}}
        >
          Contact
        </Typography>
        <div className="d-flex-align-center-justify-center flex-direction-column">
         
          <p className="mb-0px" mt={0}>
            <b>Email: </b>
            <Link href="mailto:gifts@i-am-peacekeeper.com" className='cursor-pointer'>gifts@i-am-peacekeeper.com </Link> 
          </p>
        </div>
      </Box>

      {/* Login Modal with Side-by-Side Design */}
      <Modal
        open={openLoginOptionsModal}
        onClose={() => {
          setOpenLoginOptionsModal(false);
          // Reset OTP state when closing modal
          setOtpSent(false);
          setOtp('');
          setResendTimer(0);
          setShowPasswordField(false);
          setShowOtpField(false);
          setPeacekeeperPassword('');
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openLoginOptionsModal}>
          <Box sx={modalStyle}>
            {/* Header */}
            <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5" className="green-title" sx={{ fontWeight: 600 }}>
                  I Am Peacekeeper Gifts
                </Typography>
                <IconButton
                  onClick={() => setOpenLoginOptionsModal(false)}
                  sx={{ color: 'text.secondary' }}
                >
                  <Close />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                You may browse other products directly within Shopify, no need to visit the Peacekeeper website for each individual product.
              </Typography>
            </Box>

            {/* Main Content - Side by Side */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
              {/* Left Side - Login Options */}
              <Box sx={{ 
                width: { xs: '100%', sm: '35%', md: '40%' },
                pr: { sm: 2 }, 
                borderRight: { sm: '1px solid #e0e0e0' }, 
                mb: { xs: 3, sm: 0 } 
              }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#214F7D' }}>
                    Choose Login Method
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant={selectedLoginType === 'peacekeeper' ? 'contained' : 'outlined'}
                      onClick={() => {
                        setSelectedLoginType('peacekeeper');
                        // Reset to original state: only email field and two buttons
                        setShowPasswordField(false);
                        setShowOtpField(false);
                        setOtp('');
                        setOtpSent(false);
                        setResendTimer(0);
                        setPeacekeeperPassword('');
                      }}
                      sx={{
                        background: selectedLoginType === 'peacekeeper' 
                          ? 'linear-gradient(to right, #214F7D, #3A8139)' 
                          : 'transparent',
                        color: selectedLoginType === 'peacekeeper' ? 'white' : '#214F7D',
                        borderColor: '#214F7D',
                        borderRadius: '50px',
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                          background: selectedLoginType === 'peacekeeper'
                            ? 'linear-gradient(to right, #1a3f63, #2d6329)'
                            : 'rgba(33, 79, 125, 0.1)',
                        }
                      }}
                    >
                      I Am PeaceKeeper Login
                    </Button>

                    <Button
                      variant={selectedLoginType === 'referral' ? 'contained' : 'outlined'}
                      onClick={() => {
                        setSelectedLoginType('referral');
                        // Reset PeaceKeeper login state when switching
                        setShowPasswordField(false);
                        setShowOtpField(false);
                        setOtp('');
                        setOtpSent(false);
                        setResendTimer(0);
                        setPeacekeeperPassword('');
                      }}
                      sx={{
                        background: selectedLoginType === 'referral' 
                          ? 'linear-gradient(to right, #214F7D, #3A8139)' 
                          : 'transparent',
                        color: selectedLoginType === 'referral' ? 'white' : '#214F7D',
                        borderColor: '#214F7D',
                        borderRadius: '50px',
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                          background: selectedLoginType === 'referral'
                            ? 'linear-gradient(to right, #1a3f63, #2d6329)'
                            : 'rgba(33, 79, 125, 0.1)',
                        }
                      }}
                    >
                      Login with Referral ID
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => {
                        handleExternalLink();
                        // Reset PeaceKeeper login state when switching
                        setShowPasswordField(false);
                        setShowOtpField(false);
                        setOtp('');
                        setOtpSent(false);
                        setResendTimer(0);
                        setPeacekeeperPassword('');
                      }}
                      sx={{
                        borderColor: '#214F7D',
                        color: '#214F7D',
                        borderRadius: '50px',
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                          background: 'rgba(33, 79, 125, 0.1)',
                          borderColor: '#1a3f63',
                        }
                      }}
                    >
                      Continue as Guest / Shopify Account
                    </Button>
                  </Box>
                </Box>

              {/* Right Side - Login Form */}
              <Box sx={{ 
                width: { xs: '100%', sm: '65%', md: '60%' },
                pl: { sm: 3 } 
              }}>
                  {selectedLoginType === 'peacekeeper' ? (
                    <>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#214F7D' }}>
                        PeaceKeeper Login
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Login for peacekeeper only
                      </Typography>

                      <Box component="form" onSubmit={(e) => {
                        e.preventDefault();
                        if (showPasswordField && peacekeeperPassword.trim()) {
                          handlePeacekeeperLogin(e);
                        }
                      }}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          type="email"
                          value={peacekeeperId}
                          onChange={(e) => setPeacekeeperId(e.target.value)}
                          margin="normal"
                          required
                          placeholder="Enter your email"
                          sx={{ mb: 2 }}
                        />

                        {showPasswordField && (
                          <TextField
                            fullWidth
                            label="Password"
                            type={showPeacekeeperPassword ? 'text' : 'password'}
                            value={peacekeeperPassword}
                            onChange={(e) => setPeacekeeperPassword(e.target.value)}
                            margin="normal"
                            required
                            placeholder="Enter your password"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPeacekeeperPassword(!showPeacekeeperPassword)}
                                    edge="end"
                                  >
                                    {showPeacekeeperPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 2 }}
                          />
                        )}

                        {showOtpField && (
                          <TextField
                            fullWidth
                            label="OTP"
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            margin="normal"
                            required
                            placeholder="Enter OTP"
                            inputProps={{
                              maxLength: 6,
                              style: {
                                textAlign: 'center',
                                fontSize: '1.2rem',
                                letterSpacing: '0.3rem',
                                fontWeight: '500'
                              }
                            }}
                          />
                        )}

                        <Typography variant='body2' color="text.secondary" sx={{ fontSize: '0.875rem', mb: 2 }}>Your OTP is valid for 7 minutes</Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          {/* LOGIN Button - Always visible */}
                          <Button
                            type={showPasswordField ? "submit" : "button"}
                            variant="contained"
                            size="small"
                            disabled={
                              isLoading || 
                              !peacekeeperId.trim() || 
                              (showPasswordField && !peacekeeperPassword.trim()) ||
                              (showOtpField && !otp.trim())
                            }
                            onClick={async () => {
                              if (showOtpField && otp.trim()) {
                                // If OTP field is shown and OTP is entered, verify OTP
                                await handleVerifyOtp();
                              } else if (showPasswordField && peacekeeperPassword.trim()) {
                                // If password field is shown, form will submit via type="submit"
                                // This onClick won't fire, but keeping for consistency
                              } else if (!showPasswordField && !showOtpField) {
                                // If no field is shown, show password field
                                setShowPasswordField(true);
                                setShowOtpField(false);
                                setOtp(''); // Reset OTP
                              } else if (showOtpField && !otp.trim()) {
                                // If OTP field is shown but no OTP, switch to password
                                setShowPasswordField(true);
                                setShowOtpField(false);
                                setOtp('');
                              } else if (showPasswordField) {
                                // If password field is shown, form will submit
                                // This is handled by form onSubmit
                              }
                            }}
                            sx={{
                              background: 'linear-gradient(to right, #214F7D, #3A8139)',
                              color: 'white',
                              borderRadius: '50px',
                              py: 1,
                              px: 4,
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              width: 'auto',
                              minWidth: '150px',
                              '&:hover': {
                                background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                              }
                            }}
                          >
                            {isLoading ? 'Logging in...' : (showOtpField ? 'LOGIN' : 'LOGIN')}
                          </Button>

                          {/* OR Separator - Always visible */}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              textAlign: 'center', 
                              my: 0.5, 
                              color: 'text.secondary',
                              fontWeight: 500,
                              fontSize: '0.875rem'
                            }}
                          >
                            OR
                          </Typography>

                          {/* LOGIN WITH OTP Button - Always visible */}
                          <Button
                            type="button"
                            variant="contained"
                            size="small"
                            disabled={otpLoading || !peacekeeperId.trim()}
                            onClick={async () => {
                              if (!showOtpField) {
                                // Show OTP field and send OTP
                                setShowOtpField(true);
                                setShowPasswordField(false);
                                setOtp(''); // Reset OTP when switching
                                await handleSendOtp();
                              } else if (otpSent) {
                                // If OTP is already sent, resend it
                                await handleResendOtp();
                              }
                            }}
                            startIcon={otpLoading ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{
                              background: 'linear-gradient(to right, #214F7D, #3A8139)',
                              color: 'white',
                              borderRadius: '50px',
                              py: 1,
                              px: 4,
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              width: 'auto',
                              minWidth: '150px',
                              '&:hover': {
                                background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                              }
                            }}
                          >
                            {otpLoading 
                              ? 'Sending OTP...' 
                              : (showOtpField && otpSent 
                                  ? (resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP')
                                  : 'LOGIN WITH OTP')
                            }
                          </Button>
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#214F7D' }}>
                        Login with Referral ID
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Enter your referral id to login
                      </Typography>

                      <Box component="form" onSubmit={handleReferralLogin}>
                        <TextField
                          fullWidth
                          label="Referral ID"
                          type="text"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                          margin="normal"
                          required
                          placeholder="Enter your referral id"
                          sx={{ mb: 3 }}
                          inputProps={{
                            style: { textTransform: 'uppercase' }
                          }}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoading}
                            sx={{
                              background: 'linear-gradient(to right, #214F7D, #3A8139)',
                              color: 'white',
                              borderRadius: '50px',
                              py: 1.5,
                              px: 4,
                              fontSize: '1rem',
                              fontWeight: 500,
                              width: 'auto',
                              minWidth: '150px',
                              '&:hover': {
                                background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                              }
                            }}
                          >
                            {isLoading ? 'Logging in...' : 'Login'}
                          </Button>
                        </Box>
                      </Box>
                    </>
                  )}
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  )
}

export default Gifts